// P3, the forecast (decision 143): what every customer is likely to order in the days ahead, by customer and by
// product, from the invoices, with how well the same forecast did on the last 28 days. Read only.
import type { ForecastAhead } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { Empty, ErrorBox, PageHead, QueryView } from '../components/ui';
import { addDays, formatDate, formatMoney, formatQty, todayIso, weekdayName } from '../format';
import { useDesktop } from '../hooks';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

const fill = (s: string, vars: Record<string, string | number>) => s.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? ''));

export function ForecastPage() {
  const { t } = useLocale();
  const today = todayIso();
  const [span, setSpan] = useState<7 | 14>(7);
  const [view, setView] = useState<'day' | 'customers' | 'products'>('day');
  const to = addDays(today, span - 1);
  const query = useQuery({ queryKey: qk.forecastAhead(today, to), queryFn: ({ signal }) => api.forecastAhead(today, to, signal) });
  return (
    <div className="stack">
      <PageHead title={t('forecastTitle')} back="/" />
      <p className="muted">{t('forecastHint')}</p>
      <div className="chips" role="group" aria-label={t('forecastSpan')}>
        <button type="button" className="chip" aria-pressed={span === 7} onClick={() => setSpan(7)}>
          {t('forecastNext7')}
        </button>
        <button type="button" className="chip" aria-pressed={span === 14} onClick={() => setSpan(14)}>
          {t('forecastNext14')}
        </button>
      </div>
      <QueryView query={query}>
        {(f) => (
          <>
            <Accuracy f={f} />
            <section className="card wide stack-sm">
              <div className="chips" role="tablist" aria-label={t('forecastView')}>
                <button type="button" role="tab" className="chip" aria-selected={view === 'day'} aria-pressed={view === 'day'} onClick={() => setView('day')}>
                  {t('forecastByDay')}
                </button>
                <button type="button" role="tab" className="chip" aria-selected={view === 'customers'} aria-pressed={view === 'customers'} onClick={() => setView('customers')}>
                  {t('forecastByCustomer')} ({f.customers.length})
                </button>
                <button type="button" role="tab" className="chip" aria-selected={view === 'products'} aria-pressed={view === 'products'} onClick={() => setView('products')}>
                  {t('forecastByProduct')} ({f.products.length})
                </button>
              </div>
              {view === 'day' ? <ByDay f={f} /> : view === 'customers' ? <ByCustomer f={f} /> : <ByProduct f={f} />}
            </section>
          </>
        )}
      </QueryView>
    </div>
  );
}

/**
 * By day (decision 168): for one day ahead, who is likely to order, to call the ones not in yet. Each: the chance, what
 * their order usually comes to, the days they usually order, the last order, the number to call, and Called (logged
 * on their history, so the next person sees it was done).
 */
function ByDay({ f }: { f: ForecastAhead }) {
  const { t, intlLocale } = useLocale();
  const desktop = useDesktop();
  const qc = useQueryClient();
  const today = todayIso();
  const [day, setDay] = useState(f.days[0] ?? today);
  const money = (n: number) => formatMoney(n, intlLocale);
  const onDay = (c: ForecastAhead['customers'][number]) => c.days.find((d) => d.date === day)!;
  const list = f.customers
    .filter((c) => {
      const d = c.days.find((x) => x.date === day);
      return d && (d.booked !== null || d.usual !== null);
    })
    .sort((a, b) => Number(onDay(a).booked !== null) - Number(onDay(b).booked !== null) || onDay(b).chance - onDay(a).chance || a.customerName.localeCompare(b.customerName));
  const toCall = list.filter((c) => onDay(c).booked === null);
  // marked here at once, so a second tap while the list reloads never logs the call twice (history is append-only)
  const [marked, setMarked] = useState<Set<string>>(new Set());
  const called = useMutation({
    mutationFn: (c: { customerId: string }) => api.addActivity(c.customerId, { kind: 'call', body: t('forecastCalledBody').replace('{day}', formatDate(day, intlLocale)) }),
    onMutate: (c) => setMarked((m) => new Set(m).add(c.customerId)),
    onError: (_e, c) => setMarked((m) => {
      const next = new Set(m);
      next.delete(c.customerId);
      return next;
    }),
    onSuccess: (_a, c) => {
      void qc.invalidateQueries({ queryKey: qk.activities(c.customerId) });
      void qc.invalidateQueries({ queryKey: ['crm'] });
      return qc.invalidateQueries({ queryKey: ['forecast'] });
    },
  });
  const ago = (iso: string | null) => (iso === null ? '—' : `${formatDate(iso, intlLocale, 'date')} · ${fill(t('forecastDaysAgo'), { n: Math.round((Date.parse(today) - Date.parse(iso)) / 86_400_000) })}`);
  const days = (c: ForecastAhead['customers'][number]) => (c.usualDays.length ? c.usualDays.map((d) => weekdayName(d, intlLocale)).join(', ') : '—');
  const callCell = (c: ForecastAhead['customers'][number]) => (
    <>
      {c.phone ? (
        <a href={`tel:${c.phone}`} className="nowrap-link">
          {c.contactName ? `${c.contactName} · ` : ''}
          {c.phone}
        </a>
      ) : (
        <span className="muted">{t('forecastNoPhone')}</span>
      )}{' '}
      {onDay(c).booked !== null ? null : c.calledOn === today || marked.has(c.customerId) ? (
        <span className="badge badge-ok">✓ {t('forecastCalledToday')}</span>
      ) : (
        <button type="button" className="btn btn-secondary btn-sm" disabled={called.isPending} onClick={() => called.mutate(c)}>
          {t('forecastCalled')}
        </button>
      )}
    </>
  );
  const chanceCell = (c: ForecastAhead['customers'][number]) => {
    const d = onDay(c);
    // an order in with no prices yet shows as in, without a $0.00
    return d.booked !== null ? <span className="fc-booked-text">✓ {t('forecastIn')}{d.booked > 0 ? ` ${money(d.booked)}` : ''}</span> : <>{Math.round(d.chance * 100)}% · {money(d.usual ?? 0)}</>;
  };
  return (
    <div className="stack-sm" data-testid="forecast-day">
      <div className="chips" role="group" aria-label={t('forecastPickDay')}>
        {f.days.map((d) => {
          const likely = f.customers.filter((c) => {
            const x = c.days.find((y) => y.date === d)!;
            return x.booked !== null || x.chance >= 0.5;
          });
          const dollars = f.customers.reduce((a, c) => {
            const x = c.days.find((y) => y.date === d)!;
            return a + (x.booked ?? x.chance * (x.usual ?? 0));
          }, 0);
          return (
            <button key={d} type="button" className="chip" aria-pressed={d === day} onClick={() => setDay(d)}>
              {formatDate(d, intlLocale, 'day')} · {fill(t('forecastLikelyN'), { n: likely.length })} · {money(dollars)}
            </button>
          );
        })}
      </div>
      <p className="muted">{fill(t('forecastDayHint'), { n: toCall.length, in: list.length - toCall.length })}</p>
      {called.error ? <ErrorBox error={called.error} /> : null}
      {list.length === 0 ? (
        <Empty>{t('forecastNothing')}</Empty>
      ) : desktop ? (
        <div className="table-scroll">
          <table className="data-table" aria-label={t('forecastByDay')}>
            <thead>
              <tr>
                <th scope="col">{t('customer')}</th>
                <th scope="col">{t('forecastChance')}</th>
                <th scope="col">{t('forecastUsualDays')}</th>
                <th scope="col">{t('forecastLastOrder')}</th>
                <th scope="col">{t('forecastCall')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.customerId}>
                  <td>
                    <Link to={`/customers/${c.customerId}`}>{c.customerName}</Link>
                    {c.trend ? <span className={`trend-badge trend-${c.trend}`}> {t(`trend_${c.trend}`)}</span> : null}
                    {c.items.length ? <span className="cell-sub">{c.items.slice(0, 4).map((i) => `${formatQty(i.qty)} ${i.itemName}`).join(' · ')}</span> : null}
                  </td>
                  <td>{chanceCell(c)}</td>
                  <td>{days(c)}</td>
                  <td>{ago(c.lastOrderDate)}</td>
                  <td>{callCell(c)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <ul className="list" aria-label={t('forecastByDay')}>
          {list.map((c) => (
            <li key={c.customerId} className="card stack-sm">
              <div>
                <Link to={`/customers/${c.customerId}`}>
                  <strong>{c.customerName}</strong>
                </Link>{' '}
                · {chanceCell(c)}
              </div>
              <span className="row-sub">
                {t('forecastUsualDays')}: {days(c)} · {t('forecastLastOrder')}: {ago(c.lastOrderDate)}
              </span>
              <div className="row-actions">{callCell(c)}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DayHead({ iso }: { iso: string }) {
  const { intlLocale } = useLocale();
  return (
    <th scope="col" className="num">
      {formatDate(iso, intlLocale, 'day')}
    </th>
  );
}

/** How the same forecast did on the last days, each run as if it were that morning. */
function Accuracy({ f }: { f: ForecastAhead }) {
  const { t, intlLocale } = useLocale();
  const a = f.accuracy;
  const pct = (n: number | null) => (n === null ? '—' : `${n}%`);
  return (
    <section className="card wide stack-sm" data-testid="forecast-accuracy">
      <h2>{t('forecastHowWell')}</h2>
      <p className="muted">{fill(t('forecastHowWellHint'), { days: a.days })}</p>
      <div className="fc-tiles">
        <div className="fc-tile">
          <span className="fc-tile-label">{t('forecastRight')}</span>
          <strong className="fc-tile-value">{pct(a.rightPct)}</strong>
          <span className="muted">{fill(t('forecastRightOf'), { hits: a.hits, expected: a.expected })}</span>
        </div>
        <div className="fc-tile">
          <span className="fc-tile-label">{t('forecastCaught')}</span>
          <strong className="fc-tile-value">{pct(a.caughtPct)}</strong>
          <span className="muted">{fill(t('forecastCaughtOf'), { hits: a.hits, ordered: a.ordered })}</span>
        </div>
        <div className="fc-tile">
          <span className="fc-tile-label">{t('forecastProductsOff')}</span>
          <strong className="fc-tile-value">{pct(a.productErrorPct)}</strong>
          <span className="muted">{t('forecastLowerBetter')}</span>
        </div>
        <div className="fc-tile">
          <span className="fc-tile-label">{t('forecastDollars')}</span>
          <strong className="fc-tile-value">{formatMoney(a.dollarsExpected, intlLocale)}</strong>
          <span className="muted">{fill(t('forecastDollarsActual'), { actual: formatMoney(a.dollarsActual, intlLocale) })}</span>
        </div>
      </div>
    </section>
  );
}

function ByCustomer({ f }: { f: ForecastAhead }) {
  const { t, intlLocale } = useLocale();
  const [open, setOpen] = useState<string | null>(null);
  if (!f.customers.length) return <Empty>{t('forecastNothing')}</Empty>;
  const money = (n: number) => formatMoney(n, intlLocale);
  return (
    <div className="table-scroll">
      <table className="data-table forecast-table" data-testid="forecast-customers">
        <thead>
          <tr>
            <th scope="col">{t('customer')}</th>
            <th scope="col">{t('colTrend')}</th>
            <th scope="col" className="num">{t('forecastExpected')}</th>
            {f.days.map((d) => (
              <DayHead key={d} iso={d} />
            ))}
          </tr>
        </thead>
        <tbody>
          {f.customers.map((c) => (
            <Fragment key={c.customerId}>
              <tr>
                <td>
                  <button type="button" className="link-button" aria-expanded={open === c.customerId} onClick={() => setOpen(open === c.customerId ? null : c.customerId)}>
                    {c.customerName}
                  </button>
                </td>
                <td>{c.trend ? <span className={`trend-badge trend-${c.trend}`}>{t(`trend_${c.trend}`)}</span> : null}</td>
                <td className="num">
                  <strong>{money(c.expected)}</strong>
                </td>
                {c.days.map((d) => (
                  <td key={d.date} className={`num ${d.booked !== null ? 'fc-booked' : d.chance >= 0.6 ? 'fc-likely' : d.usual !== null ? 'fc-maybe' : ''}`}>
                    {d.booked !== null ? (
                      <span title={t('forecastBooked')}>✓ {money(d.booked)}</span>
                    ) : d.usual !== null ? (
                      <span title={fill(t('forecastCellTitle'), { pct: Math.round(d.chance * 100), usual: money(d.usual) })}>
                        {Math.round(d.chance * 100)}%<span className="muted"> · {money(d.usual)}</span>
                      </span>
                    ) : (
                      ''
                    )}
                  </td>
                ))}
              </tr>
              {open === c.customerId ? (
                <tr className="fc-items">
                  <td colSpan={f.days.length + 3}>
                    {t('forecastUsualOrder')}{' '}
                    {c.items.length ? c.items.map((i) => `${formatQty(i.qty)} ${i.unit} ${i.itemName}`).join(' · ') : '—'} · <Link to={`/customers/${c.customerId}`}>{t('openAccount')}</Link>
                  </td>
                </tr>
              ) : null}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ByProduct({ f }: { f: ForecastAhead }) {
  const { t } = useLocale();
  if (!f.products.length) return <Empty>{t('forecastNothing')}</Empty>;
  return (
    <div className="table-scroll">
      <table className="data-table forecast-table" data-testid="forecast-products">
        <thead>
          <tr>
            <th scope="col">{t('product')}</th>
            {f.days.map((d) => (
              <DayHead key={d} iso={d} />
            ))}
            <th scope="col" className="num">{t('total')}</th>
          </tr>
        </thead>
        <tbody>
          {f.products.map((p) => (
            <tr key={p.itemId}>
              <td>
                <Link to={`/items/${p.itemId}`}>{p.itemName}</Link> <span className="muted">{p.unit}</span>
              </td>
              {p.days.map((q, i) => (
                <td key={f.days[i]} className="num">
                  {q >= 0.5 ? formatQty(q) : ''}
                </td>
              ))}
              <td className="num">
                <strong>{formatQty(p.total)}</strong>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
